import { useEffect, useRef, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { API } from '@/lib/api';
import { openExternalUrl } from '@/lib/nativeNavigation';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  CreditCard,
  Sparkles,
  Check,
  Zap,
  Building,
  ArrowRight
} from 'lucide-react';

const formatCurrency = (value) => `R$ ${value.toFixed(2).replace('.', ',')}`;
const TRIAL_DAYS = 7;

export default function BillingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const autoStartHandledRef = useRef(false);
  const accountSegment = user?.account_segment === 'business' ? 'business' : 'personal';
  const currentPlan = user?.plan || 'starter';
  const currentPlanStatus = user?.plan_status || 'active';
  const currentPlanName = currentPlan === 'pro' ? 'Pro' : currentPlan === 'business' ? 'Premium' : 'Starter';
  const trialEligible = !user;

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      icon: Sparkles,
      price: billingCycle === 'annual' ? formatCurrency(accountSegment === 'business' ? 9.97 : 5.97) : formatCurrency(accountSegment === 'business' ? 12.90 : 7.97),
      period: '/mês',
      description: accountSegment === 'business' ? 'Para validar a operação com até 2 grupos' : 'Para começar com clareza e controle',
      billingNote: billingCycle === 'annual'
        ? (trialEligible
          ? `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 9.97 : 5.97) * 12)} após ${TRIAL_DAYS} dias de teste`
          : `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 9.97 : 5.97) * 12)} ao confirmar a troca`)
        : (trialEligible
          ? `Cobrança mensal após ${TRIAL_DAYS} dias de teste`
          : 'Cobrança mensal imediata ao confirmar a troca'),
      savingsNote: billingCycle === 'annual' ? `Economia de ${formatCurrency((((accountSegment === 'business' ? 12.90 : 7.97) * 12) - ((accountSegment === 'business' ? 9.97 : 5.97) * 12)))} no período` : '',
      features: [
        trialEligible ? `${TRIAL_DAYS} dias de teste com cartão` : 'Troca imediata de plano sem novo período de teste',
        '1 workspace',
        'Até 2 grupos importados',
        'Até 120 resumos por mês',
        'Até 2 resumos por dia em cada grupo',
        'Até 3 perguntas por dia para a IA do grupo',
        accountSegment === 'business' ? 'Validação inicial da operação' : 'Sugestões básicas de resposta',
      ],
      cta: currentPlanStatus === 'trialing' && currentPlan === 'starter' ? 'Em teste' : currentPlan === 'starter' ? 'Plano Atual' : 'Trocar para este plano',
      current: currentPlan === 'starter',
      highlighted: false
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: Zap,
      price: billingCycle === 'annual' ? formatCurrency(accountSegment === 'business' ? 12.90 : 6.97) : formatCurrency(accountSegment === 'business' ? 19.90 : 9.97),
      period: '/mês',
      description: accountSegment === 'business' ? 'Ideal para PMEs em crescimento' : 'Feito para Você',
      billingNote: billingCycle === 'annual'
        ? (trialEligible
          ? `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 12.90 : 6.97) * 12)} após ${TRIAL_DAYS} dias de teste`
          : `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 12.90 : 6.97) * 12)} ao confirmar a troca`)
        : (trialEligible
          ? `Cobrança mensal após ${TRIAL_DAYS} dias de teste`
          : 'Cobrança mensal imediata ao confirmar a troca'),
      savingsNote: billingCycle === 'annual' ? `Economia de ${formatCurrency((((accountSegment === 'business' ? 19.90 : 9.97) * 12) - ((accountSegment === 'business' ? 12.90 : 6.97) * 12)))} no período` : '',
      features: [
        trialEligible ? `${TRIAL_DAYS} dias de teste com cartão` : 'Troca imediata de plano sem novo período de teste',
        'Grupos ilimitados',
        'Resumos ilimitados',
        'Perguntas ilimitadas para a IA',
        'Sugestões com IA avançada',
        'Histórico de 90 dias',
        'Fluxo de importação recorrente',
        'Suporte prioritário',
      ],
      cta: currentPlanStatus === 'trialing' && currentPlan === 'pro' ? 'Em teste' : currentPlan === 'pro' ? 'Plano Atual' : 'Trocar para este plano',
      current: currentPlan === 'pro',
      highlighted: true
    },
    {
      id: 'business',
      name: 'Premium',
      icon: Building,
      price: billingCycle === 'annual' ? formatCurrency(accountSegment === 'business' ? 49.90 : 29.97) : formatCurrency(accountSegment === 'business' ? 69.90 : 39.97),
      period: '/mês',
      description: accountSegment === 'business' ? 'Para times e operação' : 'Para mais usuários e uso avançado',
      billingNote: billingCycle === 'annual'
        ? (trialEligible
          ? `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 49.90 : 29.97) * 12)} após ${TRIAL_DAYS} dias de teste`
          : `Pagamento anual de ${formatCurrency((accountSegment === 'business' ? 49.90 : 29.97) * 12)} ao confirmar a troca`)
        : (trialEligible
          ? `Cobrança mensal após ${TRIAL_DAYS} dias de teste`
          : 'Cobrança mensal imediata ao confirmar a troca'),
      savingsNote: billingCycle === 'annual' ? `Economia de ${formatCurrency((((accountSegment === 'business' ? 69.90 : 39.97) * 12) - ((accountSegment === 'business' ? 49.90 : 29.97) * 12)))} no período` : '',
      features: [
        trialEligible ? `${TRIAL_DAYS} dias de teste com cartão` : 'Troca imediata de plano sem novo período de teste',
        '5 membros inclusos',
        'Resumos ilimitados',
        'Perguntas ilimitadas para a IA',
        'Workspaces múltiplos',
        'Fluxos assistidos para operação',
        'Relatórios avançados',
        'Roadmap de app e share extension',
        'Suporte dedicado',
      ],
      cta: currentPlanStatus === 'trialing' && currentPlan === 'business' ? 'Em teste' : currentPlan === 'business' ? 'Plano Atual' : 'Trocar para este plano',
      current: currentPlan === 'business',
      highlighted: false
    }
  ];

  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan) {
      toast.info(`Você já está no plano ${currentPlanName}`);
      return;
    }
    try {
      setCheckoutLoadingPlan(planId);
      const response = await fetch(`${API}/billing/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan: planId,
          billing_cycle: billingCycle,
          success_url: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}&source=billing`,
          cancel_url: `${window.location.origin}/app/billing?checkout=cancelled`,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível iniciar o checkout.');
      }
      if (data.checkout_url) {
        await openExternalUrl(data.checkout_url);
        return;
      }
      throw new Error('Checkout sem URL de redirecionamento.');
    } catch (error) {
      toast.error(error.message || 'Erro ao iniciar assinatura');
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  useEffect(() => {
    if (autoStartHandledRef.current) return;
    const shouldStartTrial = searchParams.get('start_trial') === '1';
    const planId = searchParams.get('plan');
    const requestedCycle = searchParams.get('billing_cycle');
    if (!shouldStartTrial || !planId) return;

    autoStartHandledRef.current = true;
    if (requestedCycle === 'monthly' || requestedCycle === 'annual') {
      setBillingCycle(requestedCycle);
    }

    const timer = window.setTimeout(() => {
      handleSelectPlan(planId);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [searchParams]);

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center justify-center gap-3">
            <CreditCard className="w-8 h-8 text-violet-600" />
            Assinatura
          </h1>
          <p className="text-zinc-500 mt-2 max-w-lg mx-auto">
            {accountSegment === 'business'
              ? 'Escolha o plano ideal para organizar clientes, equipe e operação com IA'
              : 'Escolha o plano ideal para acompanhar seus grupos com mais clareza'}
          </p>
        </div>

        <Card className="mb-8 border-violet-200 bg-violet-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-violet-600" />
              <div>
                <p className="text-sm font-medium text-violet-700">Plano Atual: {currentPlanName}</p>
                <p className="text-xs text-violet-600">
                  {currentPlan === 'starter'
                    ? currentPlanStatus === 'trialing'
                      ? `Seu teste do Starter está ativo. A cobrança acontece ao fim de ${TRIAL_DAYS} dias, se você não cancelar antes.`
                      : 'Seu plano Starter está ativo com foco em uso leve e controlado'
                    : currentPlanStatus === 'trialing'
                      ? `Seu teste está ativo. A cobrança acontece ao fim de ${TRIAL_DAYS} dias, se você não cancelar antes.`
                    : currentPlan === 'pro'
                      ? 'Seu plano já está com recursos ilimitados para uso individual'
                      : accountSegment === 'business'
                        ? 'Seu plano está pronto para time, operação e crescimento com IA'
                        : 'Seu plano está pronto para equipe e operação avançada'}
                </p>
              </div>
            </div>
            <Badge className="bg-violet-100 text-violet-700 border-0">
              {currentPlanStatus === 'trialing' ? 'Em teste' : currentPlanStatus === 'active' ? 'Ativo' : currentPlanStatus}
            </Badge>
          </CardContent>
        </Card>

        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center rounded-full border border-sky-100 bg-sky-50 p-1">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-sky-700'
              }`}
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-sky-700'
              }`}
            >
              Anual
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative ${
                plan.highlighted
                  ? 'border-2 border-violet-500 shadow-lg shadow-violet-100'
                  : 'border-zinc-200'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-4 py-1">
                    Mais Popular
                  </Badge>
                </div>
              )}

              {trialEligible ? (
                <div className="absolute top-4 right-4">
                  <Badge className="border-0 bg-emerald-100 text-emerald-700">
                    7 dias grátis
                  </Badge>
                </div>
              ) : null}

              <CardHeader className="text-center pt-8">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                  plan.highlighted ? 'bg-violet-100' : 'bg-zinc-100'
                }`}>
                  <plan.icon className={`w-6 h-6 ${
                    plan.highlighted ? 'text-violet-600' : 'text-zinc-600'
                  }`} />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-zinc-900">{plan.price}</span>
                  <span className="text-zinc-500">{plan.period}</span>
                  {plan.billingNote ? (
                    <p className="mt-2 text-sm text-zinc-500">{plan.billingNote}</p>
                  ) : null}
                  {plan.savingsNote ? (
                    <p className="mt-1 text-sm font-medium text-emerald-600">{plan.savingsNote}</p>
                  ) : null}
                </div>
              </CardHeader>

              <CardContent className="pb-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        plan.highlighted ? 'text-violet-600' : 'text-emerald-500'
                      }`} />
                      <span className="text-zinc-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full rounded-full ${
                    plan.current
                      ? 'bg-zinc-100 text-zinc-600 hover:bg-zinc-100 cursor-default'
                      : plan.highlighted
                        ? 'bg-zinc-900 hover:bg-zinc-800'
                        : ''
                  }`}
                  variant={plan.current ? 'ghost' : plan.highlighted ? 'default' : 'outline'}
                  disabled={plan.current || checkoutLoadingPlan === plan.id}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {checkoutLoadingPlan === plan.id ? 'Redirecionando...' : plan.cta}
                  {!plan.current && checkoutLoadingPlan !== plan.id && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <Card className="mt-12 border-zinc-200">
          <CardHeader>
            <CardTitle>Dúvidas sobre Planos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium text-zinc-900 mb-1">Posso mudar de plano a qualquer momento?</h4>
              <p className="text-sm text-zinc-600">
                Sim. Você pode fazer upgrade ou downgrade quando quiser e ajustar o ciclo de cobrança depois.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-zinc-900 mb-1">Como funciona o plano anual?</h4>
              <p className="text-sm text-zinc-600">
                No anual, o valor mensal exibido representa o custo equivalente por mês, com pagamento total do período de uma vez após o teste.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-zinc-900 mb-1">O que acontece se eu cancelar?</h4>
              <p className="text-sm text-zinc-600">
                Se cancelar durante o teste, não haverá cobrança. Após a primeira cobrança, você mantém acesso até o fim do período pago.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center text-sm text-zinc-500">
          <p>Precisa de ajuda? Entre em contato: <a href="mailto:contato@openova.com.br" className="text-violet-600 hover:underline">contato@openova.com.br</a></p>
        </div>
      </div>
    </AppLayout>
  );
}
